import { useState, useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { SiteHeader } from "@/components/site-header"
import { api } from "@/lib/api"
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Pagination } from "@/components/ui/pagination"
import { EyeIcon, CopyIcon, CheckIcon } from "lucide-react"

// ─── Types ────────────────────────────────────────────────────────────────────

type Operation = "INSERT" | "UPDATE" | "DELETE"

interface DiffEntry { from: unknown; to: unknown }

interface AuditRow {
  id: number
  tableName: string
  recordId: string
  operation: string
  changedBy: string | null
  changedByName: string | null
  changedAt: string
  diff: Record<string, DiffEntry> | null
  oldData: Record<string, unknown> | null
  newData: Record<string, unknown> | null
}

interface AuditResponse {
  data: AuditRow[]
  total: number
  page: number
  pageSize: number
  tableNames: string[]
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25

const OPERATION_LABEL: Record<Operation, string> = {
  INSERT: "Creación",
  UPDATE: "Modificación",
  DELETE: "Eliminación",
}

const OPERATION_CLASS: Record<Operation, string> = {
  INSERT: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400",
  UPDATE: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400",
  DELETE: "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400",
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatTs(iso: string): string {
  return new Date(iso).toLocaleString("es-AR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  })
}

function formatVal(v: unknown): string {
  if (v === null || v === undefined) return "—"
  if (typeof v === "boolean")        return v ? "true" : "false"
  if (typeof v === "object")         return JSON.stringify(v)
  return String(v)
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function OperationBadge({ op }: { op: string }) {
  const key = op as Operation
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${OPERATION_CLASS[key] ?? "bg-muted text-muted-foreground"}`}>
      {OPERATION_LABEL[key] ?? op}
    </span>
  )
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  function copy() {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }
  return (
    <button
      type="button"
      onClick={copy}
      className="ml-1 text-muted-foreground hover:text-foreground transition-colors"
      title="Copiar"
    >
      {copied
        ? <CheckIcon className="inline size-3 text-green-500" />
        : <CopyIcon className="inline size-3" />
      }
    </button>
  )
}

/** Renders a diff table for UPDATE operations. */
function DiffTable({ diff }: { diff: Record<string, DiffEntry> }) {
  const entries = Object.entries(diff)
  if (entries.length === 0) return <p className="text-sm text-muted-foreground">Sin cambios detectados.</p>
  return (
    <div className="rounded-md border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/60">
          <tr>
            <th className="text-left px-3 py-2 font-medium text-muted-foreground w-1/3">Campo</th>
            <th className="text-left px-3 py-2 font-medium text-muted-foreground w-1/3">Antes</th>
            <th className="text-left px-3 py-2 font-medium text-muted-foreground w-1/3">Después</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([field, { from, to }]) => (
            <tr key={field} className="border-t">
              <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground align-top">{field}</td>
              <td className="px-3 py-1.5 text-xs text-red-600 dark:text-red-400 align-top break-all">{formatVal(from)}</td>
              <td className="px-3 py-1.5 text-xs text-emerald-600 dark:text-emerald-400 align-top break-all">{formatVal(to)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Renders collapsed JSON for INSERT / DELETE snapshots. */
function JsonSnapshot({ label, data }: { label: string; data: Record<string, unknown> | null }) {
  const [open, setOpen] = useState(false)
  if (!data) return null
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 mb-1"
      >
        {open ? "Ocultar" : "Ver"} {label}
      </button>
      {open && (
        <pre className="text-[11px] bg-muted rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap break-all">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  )
}

/** Detail dialog for a single audit entry. */
function AuditDetailDialog({
  row,
  onClose,
}: {
  row: AuditRow | null
  onClose: () => void
}) {
  if (!row) return null

  const isUpdate = row.operation === "UPDATE"
  const isInsert = row.operation === "INSERT"

  return (
    <Dialog open={!!row} onOpenChange={open => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <OperationBadge op={row.operation} />
            <span className="font-mono text-sm text-muted-foreground">{row.tableName}</span>
          </DialogTitle>
          <DialogDescription>{formatTs(row.changedAt)}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm mt-1">
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Registro ID</p>
            <p className="font-mono text-xs break-all">
              {row.recordId}
              <CopyButton text={row.recordId} />
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-0.5">Usuario</p>
            <p className="text-sm">
              {row.changedByName ?? (row.changedBy
                ? <span className="font-mono text-xs">{row.changedBy.slice(0, 8)}…</span>
                : <span className="text-muted-foreground">Sistema</span>
              )}
            </p>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          {isUpdate && row.diff && (
            <div>
              <p className="text-sm font-medium mb-2">Campos modificados</p>
              <DiffTable diff={row.diff} />
            </div>
          )}

          {isInsert && (
            <JsonSnapshot label="datos creados" data={row.newData} />
          )}

          {!isInsert && (
            <JsonSnapshot label="datos anteriores (old_data)" data={row.oldData} />
          )}

          {isUpdate && (
            <JsonSnapshot label="datos posteriores (new_data)" data={row.newData} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AuditPage() {
  const [page, setPage]           = useState(1)
  const [operation, setOperation] = useState<string>("all")
  const [tableName, setTableName] = useState<string>("all")
  const [detail, setDetail]       = useState<AuditRow | null>(null)

  // Reset page when filters change
  useEffect(() => { setPage(1) }, [operation, tableName])

  const { data, isLoading } = useQuery<AuditResponse>({
    queryKey: ["audit-log", page, operation, tableName],
    queryFn: async () => {
      const query: Record<string, string> = {
        page:     String(page),
        pageSize: String(PAGE_SIZE),
      }
      if (operation !== "all") query.operation = operation
      if (tableName !== "all") query.tableName = tableName

      // Using Hono RPC client with query params
      const res = await (api["audit-log"] as any).$get({ query })
      if (!res.ok) throw new Error("Error al cargar el log de auditoría")
      return res.json() as Promise<AuditResponse>
    },
  })

  const rows       = data?.data ?? []
  const total      = data?.total ?? 0
  const tableNames = data?.tableNames ?? []
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <>
      <SiteHeader title="Log de auditoría" />

      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">

        {/* ── Description + Filters ────────────────────────────────────────── */}
        <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-end sm:justify-between">
          <p className="text-sm text-muted-foreground max-w-md">
            Registro de todas las creaciones, modificaciones y eliminaciones realizadas desde la aplicación.
          </p>

          <div className="flex items-center gap-2 shrink-0">
            {/* Tabla filter */}
            <Select value={tableName} onValueChange={(v) => setTableName(v ?? "all")}>
              <SelectTrigger className="w-40 h-8 text-sm">
                <SelectValue placeholder="Todas las tablas">
                  {tableName === "all" ? "Todas las tablas" : tableName}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las tablas</SelectItem>
                {tableNames.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Operation filter */}
            <Select value={operation} onValueChange={(v) => setOperation(v ?? "all")}>
              <SelectTrigger className="w-36 h-8 text-sm">
                <SelectValue placeholder="Todas">
                  {operation === "all" ? "Todas" : OPERATION_LABEL[operation as Operation] ?? operation}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="INSERT">Creación</SelectItem>
                <SelectItem value="UPDATE">Modificación</SelectItem>
                <SelectItem value="DELETE">Eliminación</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* ── Table ────────────────────────────────────────────────────────── */}
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-44">Fecha / Hora</TableHead>
                <TableHead className="w-28">Operación</TableHead>
                <TableHead className="w-28">Tabla</TableHead>
                <TableHead>Registro ID</TableHead>
                <TableHead>Usuario</TableHead>
                <TableHead className="w-12 text-right pr-4" />
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-16 text-muted-foreground">
                    Cargando…
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-16 text-muted-foreground">
                    No hay registros de auditoría
                  </TableCell>
                </TableRow>
              ) : rows.map(row => (
                <TableRow key={row.id}>
                  <TableCell className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                    {formatTs(row.changedAt)}
                  </TableCell>
                  <TableCell>
                    <OperationBadge op={row.operation} />
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-[11px] bg-muted px-1.5 py-0.5 rounded">
                      {row.tableName}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {row.recordId.slice(0, 8)}…
                    <CopyButton text={row.recordId} />
                  </TableCell>
                  <TableCell className="text-sm">
                    {row.changedByName ?? (
                      row.changedBy
                        ? <span className="font-mono text-xs text-muted-foreground">{row.changedBy.slice(0, 8)}…</span>
                        : <span className="text-muted-foreground/50">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right pr-3">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      title="Ver detalle"
                      onClick={() => setDetail(row)}
                    >
                      <EyeIcon className="size-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      </div>

      <AuditDetailDialog row={detail} onClose={() => setDetail(null)} />
    </>
  )
}
